import { Copy, Download, ExternalLink, FileSignature, X } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { Pagination } from '@/components/shared/Pagination';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useSignatures } from '@/hooks/useSignatures';
import { signaturesApi } from '@/lib/api/signatures.api';

import type { SignatureRequest } from '@/lib/api/signatures.api';

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'CREATED', label: 'Created' },
  { value: 'SENT', label: 'Sent' },
  { value: 'OPENED', label: 'Opened' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'REJECTED', label: 'Rejected' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const STATUS_BADGE_CLASSES: Record<SignatureRequest['status'], string> = {
  CREATED: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  SENT: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  OPENED: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  COMPLETED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
  REJECTED: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  FAILED: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  CANCELLED: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

const DEFAULT_LIMIT = 25;

export function SignaturesPage() {
  usePageTitle('Digital Signatures');
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();

  const status = searchParams.get('status') || '';
  const page = Number(searchParams.get('page')) || 1;
  const limit = Number(searchParams.get('limit')) || DEFAULT_LIMIT;

  const { data, isLoading } = useSignatures({
    status: status || undefined,
    page,
    limit,
  });

  const requests = data?.data ?? [];
  const pagination = data?.pagination;

  const updateParams = (updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
      }
      return next;
    });
  };

  const clearFilters = () => {
    setSearchParams(new URLSearchParams());
  };

  const hasActiveFilters = Boolean(status);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const copySigningLink = async (request: SignatureRequest) => {
    if (!request.signing_url) return;
    await navigator.clipboard.writeText(request.signing_url);
    toast({ title: 'Signing link copied to clipboard' });
  };

  const downloadSigned = async (request: SignatureRequest) => {
    try {
      const { downloadUrl } = await signaturesApi.getDownloadUrl(request.id);
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch {
      toast({ title: 'Failed to fetch the signed document', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <p className="text-muted-foreground text-sm tracking-[0.3em] uppercase">Signatures</p>
        <h1 className="text-3xl font-bold tracking-tight">Digital Signatures</h1>
        <p className="text-muted-foreground max-w-3xl">
          Documents awaiting your signature, and those already signed
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>All Signature Requests</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="mb-6 flex flex-wrap items-end gap-4">
            <div className="min-w-[160px]">
              <label
                htmlFor="status-filter"
                className="text-muted-foreground mb-1 block text-sm font-medium"
              >
                Status
              </label>
              <select
                id="status-filter"
                value={status}
                onChange={(e) => updateParams({ status: e.target.value || null, page: null })}
                className="focus:ring-primary bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none dark:border-slate-700"
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="mr-1 h-4 w-4" />
                Clear filters
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="flex min-h-[200px] items-center justify-center">
              <div className="text-center">
                <div className="border-primary mx-auto h-8 w-8 animate-spin rounded-full border-b-2" />
                <p className="text-muted-foreground mt-2 text-sm">Loading signature requests...</p>
              </div>
            </div>
          ) : requests.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
              <FileSignature className="text-muted-foreground mb-3 h-10 w-10" />
              <p className="text-muted-foreground text-sm">
                {hasActiveFilters
                  ? 'No signature requests match the current filters.'
                  : 'No documents have been sent for signature yet.'}
              </p>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" className="mt-3" onClick={clearFilters}>
                  <X className="mr-1 h-4 w-4" />
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="text-muted-foreground text-left text-xs tracking-wide uppercase">
                    <th className="border-b px-3 py-2 font-medium">Date</th>
                    <th className="border-b px-3 py-2 font-medium">Document</th>
                    <th className="border-b px-3 py-2 font-medium">Recipient</th>
                    <th className="border-b px-3 py-2 font-medium">Status</th>
                    <th className="border-b px-3 py-2 font-medium">Signed</th>
                    <th className="border-b px-3 py-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((request) => (
                    <tr key={request.id} className="border-b last:border-b-0 dark:border-slate-700">
                      <td className="px-3 py-3 text-xs whitespace-nowrap text-gray-500 dark:text-gray-400">
                        {formatDate(request.created_at)}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-col gap-0.5">
                          <span className="font-medium">{request.title}</span>
                          {request.error_message && (
                            <span
                              className="text-destructive max-w-[280px] truncate text-[11px]"
                              title={request.error_message}
                            >
                              {request.error_message}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-col gap-0.5">
                          <span>{request.recipient_name}</span>
                          <span className="text-muted-foreground text-xs">
                            {request.recipient_email}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE_CLASSES[request.status]}`}
                        >
                          {request.status.charAt(0) + request.status.slice(1).toLowerCase()}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-xs whitespace-nowrap text-gray-600 dark:text-gray-300">
                        {formatDate(request.completed_at) ?? (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1">
                          {request.signing_url && request.status !== 'COMPLETED' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0"
                                title="Copy signing link"
                                onClick={() => copySigningLink(request)}
                              >
                                <Copy className="h-3.5 w-3.5" />
                              </Button>
                              <a
                                href={request.signing_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Open signing page"
                                className="text-muted-foreground hover:text-foreground inline-flex h-7 w-7 items-center justify-center rounded-md"
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            </>
                          )}
                          {request.status === 'COMPLETED' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2"
                              title="Download signed PDF"
                              onClick={() => downloadSigned(request)}
                            >
                              <Download className="mr-1 h-3.5 w-3.5" />
                              <span className="text-xs">Signed PDF</span>
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pagination && (
            <div className="mt-4">
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                total={pagination.total}
                limit={pagination.limit}
                onPageChange={(p) => updateParams({ page: p > 1 ? String(p) : null })}
                resourceName="signature requests"
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
